import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { GalleryPagination } from 'features/content-library/components/content-types/GalleryFile/GalleryPagination';
import { ToggleThumbnailsControl } from 'features/content-library/components/content-types/GalleryFile/ToggleThumbnailsControl';
import React, { useCallback, useEffect, useRef, useImperativeHandle } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react';
import { Keyboard, Mousewheel, Pagination, Virtual } from 'swiper/modules';
import { breakpointValues } from 'theme/themeBaseOptions';
import { contentLibraryChangeFilePageRequested } from 'features/content-library/actions';
import { ContentLibraryMultiplePageOpenedFile } from 'features/content-library/types';

import {
  selectContentLibraryOpenedFileThumbnailsActive,
  selectActiveContentPage,
} from 'features/content-library/contentLibrarySlice';
import { Slide, SlideImg } from 'features/content-library/components/content-types/styled';
import { useBreakpoints } from 'hooks/useBreakpoints';
import ThumbnailList from 'features/content-library/components/content-types/GalleryFile/thumbnails/ThumbnailList';

import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/pagination';
import 'swiper/css/virtual';

const StyledContainer = styled(Box)<{ height?: number }>(({ height }) => ({
  '.swiper': {
    width: '100%',
    height: '100%',
  },
  ...(height && {
    '.swiper-wrapper': {
      maxHeight: `${height}px`,
    },
  }),
}));

const handleKeyboard = (slider: SwiperClass, keyCode: string) => {
  switch (keyCode) {
    case '37':
      slider.slidePrev();
      break;
    case '39':
      slider.slideNext();
      break;
    default:
      break;
  }
};

export type GalleryFileProps = {
  file: ContentLibraryMultiplePageOpenedFile;
  size: { width?: number; height?: number };
  renderSlide?: (file: any, index: number) => React.ReactNode;
  allowTouchMove?: boolean;
  renderOverlay?: () => React.ReactNode;
  onSlideChangeStart?: () => void;
  onSlideChangeEnd?: () => void;
  isPreview?: boolean;
};

const SwiperLazyPreloader = styled('div')(({ theme }) => ({
  '--swiper-preloader-color': theme.palette.primary.main,
}));

const swiperConfig = {
  keyboard: {
    enabled: true,
  },
  modules: [Keyboard, Mousewheel, Pagination, Virtual],
};

const GalleryFile = React.forwardRef<SwiperClass, GalleryFileProps>(
  (
    {
      file,
      size,
      renderSlide: renderSlideProp,
      allowTouchMove = true,
      renderOverlay,
      onSlideChangeStart,
      onSlideChangeEnd,
      isPreview = false,
    }: GalleryFileProps,
    ref
  ) => {
    const dispatch = useAppDispatch();

    const swiperRef = useRef<SwiperClass | null>(null);
    const activeIndexRef = useRef(0);
    const ignoreUpdatesRef = useRef(false);

    useImperativeHandle(ref, () => swiperRef.current as SwiperClass, []);

    const currentPage = useAppSelector((state) => selectActiveContentPage(state, isPreview));
    const thumbnailsActive = useAppSelector(selectContentLibraryOpenedFileThumbnailsActive);

    const { isMobile } = useBreakpoints();

    useEffect(() => {
      if (currentPage !== activeIndexRef.current) {
        if (swiperRef.current) {
          activeIndexRef.current = currentPage;

          ignoreUpdatesRef.current = true;
          swiperRef.current.slideTo(currentPage);
        }
      }
    }, [currentPage]);

    const goToPage = useCallback((idx: number) => {
      if (swiperRef.current) {
        swiperRef.current.slideTo(idx);
      }
    }, []);

    const handleActiveSlideChange = (e: SwiperClass) => {
      if (ignoreUpdatesRef.current) {
        ignoreUpdatesRef.current = false;
      } else {
        activeIndexRef.current = e.activeIndex;

        dispatch(
          contentLibraryChangeFilePageRequested({
            id: file.id,
            page: e.activeIndex,
          })
        );
      }
    };

    const showThumbs = !isMobile && size?.width && size.width > breakpointValues.md;

    const renderSlide = useCallback(
      (imgUrl: string, index: number) => {
        if (renderSlideProp) {
          return renderSlideProp(file, index);
        }

        return <SlideImg src={imgUrl} loading="lazy" />;
      },
      [renderSlideProp, file]
    );

    return (
      <StyledContainer
        height={size.height}
        id="slide-container"
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          top: 0,
          left: 0,
          display: 'flex',
        }}
      >
        {showThumbs && thumbnailsActive ? (
          <ThumbnailList pages={file.pages} goToPage={goToPage} currentPage={currentPage} />
        ) : null}
        <div
          style={{
            flexGrow: '1',
            position: 'relative',
            pointerEvents: 'all',
          }}
        >
          {showThumbs ? <ToggleThumbnailsControl /> : null}
          <GalleryPagination goToPage={goToPage} currentPage={currentPage} />
          <Swiper
            direction="vertical"
            spaceBetween={10}
            slidesPerView={1}
            mousewheel
            allowTouchMove={allowTouchMove}
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
            }}
            onKeyPress={handleKeyboard}
            onSlideChange={handleActiveSlideChange}
            initialSlide={currentPage}
            key="gallery-present"
            onSlideChangeTransitionStart={onSlideChangeStart}
            onSlideChangeTransitionEnd={onSlideChangeEnd}
            {...swiperConfig}
            virtual
          >
            {file.pages.map((img, idx) => (
              <SwiperSlide key={idx} virtualIndex={idx}>
                <Slide sx={{ height: 'calc(100% - 12px)' }}>
                  {renderSlide(img.url, idx)}
                  <Box />
                  <SwiperLazyPreloader className="swiper-lazy-preloader" />
                </Slide>
              </SwiperSlide>
            ))}
          </Swiper>
          {typeof renderOverlay === 'function' ? renderOverlay() : null}
        </div>
      </StyledContainer>
    );
  }
);

export default GalleryFile;
